class ProductForm {
    constructor() {
      this.form = document.querySelector('.product-options-form');
      this.optionRadios = this.form?.querySelectorAll('.product-option-radio');
      this.addButton = this.form?.querySelector('.product-options-add-to-cart');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      
      // Get product data from window (set in main-product.liquid)
      this.variants = window.productVariants || [];
      this.options = window.productOptions || [];
      this.media = window.productMedia || [];
      
      // Gallery elements
      this.galleryContainer = document.querySelector('.product-gallery__container');
      this.galleryItems = document.querySelectorAll('.product-gallery__item');
      
      console.log('ProductForm initialized', {
        form: this.form,
        optionRadios: this.optionRadios?.length,
        variants: this.variants.length,
        options: this.options
      });
      
      this.init();
    }
  
    init() {
      if (!this.form) {
        console.error('ProductForm: Form not found');
        return;
      }

      this.isContactMode = this.form.dataset.mode === 'contact';
  
      this.setupVariantHandlers();
      if (!this.isContactMode) {
        this.setupQuantityHandlers();
        this.setupFormSubmission();
      }
      
      // Initialize with default variant after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.updateVariantFromOptions();
        // Also check initial price for discounts
        this.checkInitialPriceForDiscount();
        // Initialize gallery for default variant
        const defaultVariant = this.variants.find(v => v.id === parseInt(this.variantInput?.value)) || this.variants[0];
        if (defaultVariant) {
          this.updateGalleryForVariant(defaultVariant.id);
        }
      }, 100);
    }
  
    setupVariantHandlers() {
      if (!this.optionRadios || this.optionRadios.length === 0) {
        console.warn('ProductForm: No option radios found');
        return;
      }
  
      // Also add click handlers to labels for better UX
      const optionLabels = this.form.querySelectorAll('.product-option-label');
      optionLabels.forEach(label => {
        label.addEventListener('click', (e) => {
          // The radio will be checked automatically via the label's 'for' attribute
          // But we need to trigger the change event
          const radioId = label.getAttribute('for');
          const radio = this.form.querySelector(`#${radioId}`);
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
  
      this.optionRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          console.log('Option radio changed:', radio.value, radio.dataset.optionPosition);
          this.handleOptionChange(radio);
        });
      });
    }
  
    handleOptionChange(clickedRadio) {
      const optionPosition = parseInt(clickedRadio.dataset.optionPosition);
      
      // Remove selected class from all labels in this option group
      const optionRow = clickedRadio.closest('.product-options-row');
      const allLabels = optionRow?.querySelectorAll('.product-option-label');
      if (allLabels) {
        allLabels.forEach(label => {
          label.classList.remove('selected');
        });
      }
      
      // Add selected class to the clicked radio's label
      const clickedLabel = this.form.querySelector(`label[for="${clickedRadio.id}"]`);
      if (clickedLabel) {
        clickedLabel.classList.add('selected');
      }
      
      // Update variant based on all selected options
      this.updateVariantFromOptions();
    }
  
    updateVariantFromOptions() {
      // Get all selected option values
      const selectedOptions = [];
      this.options.forEach((option, index) => {
        const selectedRadio = this.form.querySelector(
          `input[name="option-${index}"]:checked`
        );
        if (selectedRadio) {
          selectedOptions[index] = selectedRadio.value;
        }
      });
      
      console.log('Selected options:', selectedOptions);
      
      // Find matching variant
      const matchingVariant = this.findMatchingVariant(selectedOptions);
      
      if (matchingVariant) {
        // Update variant ID in form
        if (this.variantInput) {
          this.variantInput.value = matchingVariant.id;
        }
        
        // Update price display in header (with discount check)
        this.updatePriceDisplayWithDiscount(matchingVariant);
        
        // Update gallery to show variant-specific images
        this.updateGalleryForVariant(matchingVariant.id);
        
        // Update add to cart button price (skip in contact/custom-order mode)
        const price = this.formatMoney(matchingVariant.price);
        if (this.addButton && !this.isContactMode) {
          this.addButton.textContent = `Add to Cart – ${price}`;
          this.addButton.disabled = !matchingVariant.available;
        }
        
        // Update any other price elements
        const priceElements = this.form.querySelectorAll('.purchase-price');
        priceElements.forEach(element => {
          element.textContent = price;
        });
        
        console.log('Updated variant to:', matchingVariant.id, 'with price:', price, 'available:', matchingVariant.available);
      } else {
        console.warn('No matching variant found for selected options:', selectedOptions);
        if (this.addButton && !this.isContactMode) {
          this.addButton.disabled = true;
        }
      }
    }
  
    findMatchingVariant(selectedOptions) {
      // Match variant by comparing option values
      // Make sure we have values for all options
      const hasAllOptions = selectedOptions.length === this.options.length && 
                            selectedOptions.every(val => val !== undefined);
      
      if (!hasAllOptions) {
        console.log('Not all options selected yet:', selectedOptions);
        return null;
      }
      
      return this.variants.find(variant => {
        return selectedOptions.every((selectedValue, index) => {
          // Shopify variant options are in variant.option1, variant.option2, etc.
          const optionKey = `option${index + 1}`;
          return variant[optionKey] === selectedValue;
        });
      });
    }
  
    async checkInitialPriceForDiscount() {
      // Check if the initial variant (from page load) has a discount in cart
      const initialVariant = this.variants.find(v => {
        // Find the first checked variant or default
        const selectedOptions = [];
        this.options.forEach((option, index) => {
          const selectedRadio = this.form?.querySelector(
            `input[name="option-${index}"]:checked`
          );
          if (selectedRadio) {
            selectedOptions[index] = selectedRadio.value;
          }
        });
        
        return selectedOptions.every((selectedValue, index) => {
          const optionKey = `option${index + 1}`;
          return v[optionKey] === selectedValue;
        });
      });
      
      if (initialVariant) {
        await this.updatePriceDisplayWithDiscount(initialVariant);
      }
    }
    
    async updatePriceDisplayWithDiscount(variant) {
      const priceContainer = document.querySelector('.product-info__price');
      if (!priceContainer) return;
      
      // First check for compare_at_price (manual sale price)
      const hasCompareAtPrice = variant.compare_at_price && variant.compare_at_price > variant.price;
      
      if (hasCompareAtPrice) {
        // Use compare_at_price if available
        this.updatePriceDisplay(variant, variant.compare_at_price, variant.price);
        return;
      }
      
      // Otherwise, check cart for discounted price
      try {
        const cartItem = await this.getCartItemPrice(variant.id);
        if (cartItem && cartItem.original_line_price > cartItem.final_line_price) {
          // Discount found in cart
          const originalPrice = cartItem.original_line_price;
          const discountedPrice = cartItem.final_line_price;
          this.updatePriceDisplay(variant, originalPrice, discountedPrice);
        } else {
          // No discount, show regular price
          this.updatePriceDisplay(variant, null, variant.price);
        }
      } catch (error) {
        console.warn('Could not fetch cart price, using variant price:', error);
        // Fallback to variant price
        this.updatePriceDisplay(variant, null, variant.price);
      }
    }
    
    async getCartItemPrice(variantId) {
      try {
        // Fetch current cart
        const response = await fetch('/cart.js');
        if (!response.ok) return null;
        
        const cart = await response.json();
        
        // Find item with this variant ID
        const cartItem = cart.items.find(item => item.variant_id === variantId);
        
        if (cartItem) {
          // Item is already in cart, use its pricing
          return {
            original_line_price: cartItem.original_line_price,
            final_line_price: cartItem.final_line_price
          };
        }
        
        // Item not in cart - we can't know the discounted price until it's added
        // Return null to use variant price
        return null;
      } catch (error) {
        console.error('Error fetching cart item price:', error);
        return null;
      }
    }
    
    updatePriceDisplay(variant, originalPrice, salePrice) {
      const priceContainer = document.querySelector('.product-info__price');
      if (!priceContainer) return;
      
      const hasDiscount = originalPrice && originalPrice > salePrice;
      
      if (hasDiscount) {
        // Remove existing price elements
        priceContainer.innerHTML = '';
        
        // Create new elements
        const originalPriceEl = document.createElement('span');
        originalPriceEl.className = 'product-info__price-original';
        originalPriceEl.textContent = this.formatMoney(originalPrice);
        
        const salePriceEl = document.createElement('span');
        salePriceEl.className = 'product-info__price-sale';
        salePriceEl.textContent = this.formatMoney(salePrice);
        
        priceContainer.appendChild(originalPriceEl);
        priceContainer.appendChild(salePriceEl);
      } else {
        // Remove discount elements if they exist
        priceContainer.innerHTML = '';
        
        // Create current price
        const currentPrice = document.createElement('span');
        currentPrice.className = 'product-info__price-current';
        currentPrice.textContent = this.formatMoney(salePrice);
        
        priceContainer.appendChild(currentPrice);
      }
    }

    updateGalleryForVariant(variantId) {
      if (!this.galleryContainer || !this.galleryItems.length) return;
      
      // Find media items associated with this variant
      const variantMediaItems = [];
      const defaultMediaItems = [];
      
      this.galleryItems.forEach(item => {
        const variantIds = item.dataset.variantIds;
        const isDefault = item.dataset.isDefault === 'true';
        
        if (variantIds) {
          // Check if this media is associated with the selected variant
          const associatedVariants = variantIds.split(',').map(id => parseInt(id.trim()));
          if (associatedVariants.includes(variantId)) {
            variantMediaItems.push(item);
          }
        } else if (isDefault) {
          // Media with no variant association (default/shared media)
          defaultMediaItems.push(item);
        }
      });
      
      // Determine which media to show
      // If variant has specific media, show only those. Otherwise show default/shared media.
      const mediaToShow = variantMediaItems.length > 0 ? variantMediaItems : defaultMediaItems;
      
      // If no specific media found, show all media
      const finalMediaToShow = mediaToShow.length > 0 ? mediaToShow : Array.from(this.galleryItems);
      
      // Don't hide images - just scroll to the first variant-specific image
      // Mark which items are associated with this variant
      this.galleryItems.forEach(item => {
        if (finalMediaToShow.includes(item)) {
          item.classList.add('active');
        } else {
          item.classList.remove('active');
        }
      });
      
      // Scroll gallery to show the first image for this variant
      if (finalMediaToShow.length > 0 && this.galleryContainer) {
        const firstVariantImage = finalMediaToShow[0];
        this.scrollToGalleryItem(firstVariantImage);
      }
      
      // If we have a gallery scroll effect, trigger it to update
      if (typeof window.galleryScrollEffect !== 'undefined' && window.galleryScrollEffect) {
        // Trigger gallery update if the effect is initialized
        setTimeout(() => {
          // Scroll to first visible item
          const firstVisible = finalMediaToShow[0];
          if (firstVisible && this.galleryContainer) {
            // Update gallery scroll position if needed
            const event = new CustomEvent('variant:changed', { detail: { variantId } });
            document.dispatchEvent(event);
          }
        }, 100);
      }
      
      console.log('Updated gallery for variant:', variantId, 'showing', finalMediaToShow.length, 'media items');
    }
    
    scrollToGalleryItem(targetItem) {
      if (!targetItem || !this.galleryContainer) return;
      
      // Get the index of this item in the gallery
      const allItems = Array.from(this.galleryItems);
      const itemIndex = allItems.indexOf(targetItem);
      
      if (itemIndex === -1) return;
      
      // On desktop, use GSAP to scroll the gallery container
      if (window.innerWidth >= 768 && typeof gsap !== 'undefined') {
        const viewportHeight = window.innerHeight;
        const targetY = -(itemIndex * viewportHeight);
        
        // Animate the gallery container to the target position
        gsap.to(this.galleryContainer, {
          y: targetY,
          duration: 0.6,
          ease: 'power2.out'
        });
      } else {
        // On mobile, scroll the item into view
        targetItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    formatMoney(cents) {
      // Use Shopify's money format if available, otherwise fallback
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, '{{ shop.money_format }}');
      }
      // Fallback: Simple money formatter
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(cents / 100);
    }

    setupFormSubmission() {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    setupQuantityHandlers() {
      // Quantity input is now handled by the quantity-input snippet's own JavaScript
      // We can listen to the quantity-change event if needed
      const qtyInput = this.form?.querySelector('.quantity-input__field');
      
      if (qtyInput) {
        // Listen to the custom quantity-change event from the quantity-input component
        qtyInput.addEventListener('quantity-change', (e) => {
          // Quantity input handles its own validation and updates
          // This is just if you need to do something else when quantity changes
          console.log('Quantity changed to:', e.detail.value);
        });
      }
    }
  
    async addToCart() {
      if (!this.addButton) return;
  
      const originalText = this.addButton.textContent;
      this.addButton.disabled = true;
      this.addButton.textContent = 'Adding...';
  
      try {
        const formData = new FormData(this.form);
        
        // Debug: Check what variant ID is being sent
        console.log('Variant ID being sent:', this.variantInput ? this.variantInput.value : 'NOT FOUND');
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
  
        if (response.ok) {
          console.log('Product added to cart successfully');
          this.addButton.textContent = 'Added!';
          
          // Update cart drawer - wait for it to be available
          await this.updateCartAndOpen();
          
          // Update cart count from the response
          const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count');
          cartCountElements.forEach(element => {
            const currentCount = parseInt(element.textContent) || 0;
            element.textContent = currentCount + 1;
          });
          
          setTimeout(() => {
            this.addButton.textContent = originalText;
            this.addButton.disabled = false;
          }, 2000);
        } else {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || 'Failed to add product to cart');
        }
      } catch (error) {
        console.error('Error adding product to cart:', error);
        this.addButton.textContent = 'Error - Try Again';
        setTimeout(() => {
          this.addButton.textContent = originalText;
          this.addButton.disabled = false;
        }, 3000);
      }
    }

    async updateCartAndOpen() {
      // Try to update cart drawer
      if (typeof updateCartDrawer === 'function') {
        await updateCartDrawer();
      } else {
        // Wait a bit for the function to load
        await new Promise(resolve => setTimeout(resolve, 100));
        if (typeof updateCartDrawer === 'function') {
          await updateCartDrawer();
        }
      }
      
      // Open cart drawer - try multiple methods
      if (typeof openCartDrawer === 'function') {
        openCartDrawer();
      } else {
        // Wait a bit for the function to load
        await new Promise(resolve => setTimeout(resolve, 100));
        if (typeof openCartDrawer === 'function') {
          openCartDrawer();
        } else {
          // Fallback: open drawer directly
          const drawer = document.getElementById('cart-drawer') || document.querySelector('.cart-drawer');
          if (drawer) {
            drawer.classList.add('is-open');
            document.documentElement.classList.add('disable-overflow-y');
          } else {
            console.warn('Cart drawer element not found');
          }
        }
      }
    }
  }
  
  // Initialize when DOM is loaded
  document.addEventListener('DOMContentLoaded', () => {
    console.log('DOM loaded, initializing ProductForm');
    console.log('Available forms:', document.querySelectorAll('form'));
    console.log('Product options form:', document.querySelector('.product-options-form'));
    new ProductForm();
    
    // Small delay to ensure layout is calculated
    setTimeout(() => {
      // GalleryScrollEffect is initialized in its own file
    }, 100);
  });